import { useCallback, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'
import type { GridQuery, Severity } from '../api/client'

/**
 * Keeps the register's filters in the URL rather than in component state.
 *
 * This is what makes the detail page viable. Without it, navigating to a vehicle
 * and pressing Back would drop the user on an unfiltered page 1 — worse than the
 * drawer it replaced. With it, Back restores the exact view, and a filtered
 * register becomes a link somebody can send to a colleague:
 *
 *   /?obligationTypeId=3&obligationSeverity=5&siteId=12
 *   "every vehicle with expired fitness at this site"
 *
 * Defaults are omitted from the URL. Writing page=1 on first load produces an
 * ugly address for the commonest case and an extra history entry for no reason.
 */

const DEFAULT_PAGE_SIZE = 40

function num(params: URLSearchParams, key: string): number | undefined {
  const raw = params.get(key)
  if (raw === null || raw === '') return undefined

  const value = Number(raw)
  // A hand-edited or truncated URL must not put NaN into a query string the API
  // then rejects — treat anything unparseable as "filter absent".
  return Number.isFinite(value) ? value : undefined
}

/**
 * A tri-state flag from the URL: true, false, or not filtered at all.
 *
 * Absent is not false. "Show me vehicles with no loan" and "do not filter on loans"
 * are different questions, and only an explicit "false" asks the first.
 */
function bool(params: URLSearchParams, key: string): boolean | undefined {
  const raw = params.get(key)
  if (raw === null || raw === '') return undefined
  return raw === 'true'
}

function severity(params: URLSearchParams, key: string): Severity | undefined {
  const value = num(params, key)
  return value !== undefined && value >= 0 && value <= 5 ? (value as Severity) : undefined
}

/** Same shape as React's setState so existing `setQuery(q => …)` call sites work unchanged. */
type SetQuery = (next: GridQuery | ((prev: GridQuery) => GridQuery)) => void

export function useGridQueryParams(): [GridQuery, SetQuery] {
  const [params, setParams] = useSearchParams()

  const query = useMemo<GridQuery>(() => {
    const siteIds = params
      .get('siteIds')
      ?.split(',')
      .map(Number)
      .filter(Number.isFinite)

    // Trimmed but NOT canonicalised here. The procedure strips spaces, dashes, dots
    // and slashes with one rule; doing it here as well would be a second copy of
    // that rule, and the two drifting apart means a pasted "MH-12-EQ-3823" matching
    // under one filter and not the other.
    const registrations = params
      .get('registrations')
      ?.split(',')
      .map((r) => r.trim())
      .filter(Boolean)

    return {
      page: num(params, 'page') ?? 1,
      pageSize: num(params, 'pageSize') ?? DEFAULT_PAGE_SIZE,
      siteId: num(params, 'siteId'),
      vehicleTypeId: num(params, 'vehicleTypeId'),
      search: params.get('search') || undefined,
      worstSeverity: severity(params, 'worstSeverity'),
      hasSeverity: severity(params, 'hasSeverity'),
      obligationTypeId: num(params, 'obligationTypeId'),
      obligationSeverity: severity(params, 'obligationSeverity'),
      // Expiry window, for a drill-down from the dashboard's runway. Passed
      // through as text: the API takes ISO dates, and re-parsing a date here only
      // creates a second place for a timezone to shift it.
      expiryFrom: params.get('expiryFrom') || undefined,
      expiryTo: params.get('expiryTo') || undefined,
      siteIds: siteIds?.length ? siteIds : undefined,

      // A pasted list of registrations. In the URL like everything else, so a
      // lookup of forty vehicles is a link somebody can send.
      registrations: registrations?.length ? registrations : undefined,

      fuelType: params.get('fuelType') || undefined,
      rcStatus: params.get('rcStatus') || undefined,
      financer: params.get('financer') || undefined,
      emissionNorms: params.get('emissionNorms') || undefined,
      isFinanced: bool(params, 'isFinanced'),
      source: num(params, 'source') as GridQuery['source'],
      onlyOverridden: params.get('onlyOverridden') === '1' || undefined,
      // Only 1 and 2 mean anything; anything else in a hand-edited URL is treated
      // as "filter absent" rather than passed through to be rejected by the API.
      documentFilter: [1, 2].includes(num(params, 'documentFilter') ?? 0)
        ? (num(params, 'documentFilter') as GridQuery['documentFilter'])
        : undefined,
      documentTypeId: num(params, 'documentTypeId'),
      sortBy: params.get('sortBy') || undefined,
      sortDescending: params.get('sortDescending') === '1' || undefined,
    }
  }, [params])

  const setQuery = useCallback<SetQuery>(
    (update) => {
      const next = typeof update === 'function' ? update(query) : update
      const out = new URLSearchParams()

      const put = (key: string, value: unknown) => {
        if (value === undefined || value === null || value === '') return
        out.set(key, String(value))
      }

      if ((next.page ?? 1) !== 1) put('page', next.page)
      if ((next.pageSize ?? DEFAULT_PAGE_SIZE) !== DEFAULT_PAGE_SIZE) put('pageSize', next.pageSize)

      put('siteId', next.siteId)
      put('vehicleTypeId', next.vehicleTypeId)
      put('search', next.search)
      put('worstSeverity', next.worstSeverity)
      put('hasSeverity', next.hasSeverity)
      put('obligationTypeId', next.obligationTypeId)
      put('obligationSeverity', next.obligationSeverity)
      put('siteIds', next.siteIds?.length ? next.siteIds.join(',') : undefined)
      put(
        'registrations',
        next.registrations?.length ? next.registrations.join(',') : undefined,
      )
      put('fuelType', next.fuelType)
      put('rcStatus', next.rcStatus)
      put('financer', next.financer)
      put('emissionNorms', next.emissionNorms)
      put('isFinanced', next.isFinanced)
      put('source', next.source)
      if (next.onlyOverridden) put('onlyOverridden', '1')
      put('documentFilter', next.documentFilter)
      // Meaningless without the filter it scopes, so it is dropped rather than
      // left in the URL to reappear when the filter is switched back on.
      if (next.documentFilter) put('documentTypeId', next.documentTypeId)
      put('sortBy', next.sortBy)
      if (next.sortDescending) put('sortDescending', '1')

      // replace, not push: typing in the search box or stepping through pages
      // should not stack up history entries the user has to Back through one at
      // a time to escape the register.
      setParams(out, { replace: true })
    },
    [setParams, query],
  )

  return [query, setQuery]
}
